/**
 * AccountDeletionPage — 외부 공개용 "계정 및 데이터 삭제 안내" 단독 페이지.
 *
 * URL(웹 공개): /legal/account-deletion
 *
 * 배경: Google Play 데이터 보안 양식은 "계정 삭제 요청 URL"을 요구하며,
 *   앱을 삭제한(로그인 불가) 사용자도 접근 가능한 공개 URL이어야 한다. prod 웹은 VITE_WEB_PUBLIC=false 라
 *   /gift/* 외 전 경로가 ComingSoon 으로 차단되므로, webGateRoutes 에 이 경로만 예외로 노출한다.
 *   (개인정보처리방침 /legal/privacy 와 동일 패턴.)
 *
 * 데이터 소스: 어드민 약관 관리(TERMS_DIV = ACCOUNT_DELETION). 유저 BE
 *   GET /terms/ACCOUNT_DELETION/latest 의 본문(HTML)을 sanitize 후 렌더한다.
 *   404 / BE 미가용 → 아래 정적 fallback. (SQL 211/212 로 분류코드+본문 시드)
 *
 * Google 요구 3요소: ① 앱/개발자 이름, ② 삭제 요청 단계, ③ 삭제/보관 데이터 유형 + 보관 기간.
 * 콘텐츠 근거(코드): 탈퇴 즉시 WITHDRAWN + 세션 폐기 → 7일 후 PII 익명화(T-1 스케줄러).
 *   결제 기록은 전자상거래법상 5년 보관(개인정보처리방침 제7조).
 *
 * ⚠ 고객센터 이메일은 어드민 약관 본문에서 실제 운영 CS 이메일로 교체 필요(현재 임시값).
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLatestTermsQuery } from "../../hooks/useTermsQueries";
import { sanitizeHtml } from "../../lib/sanitizeHtml";
import { track } from "../../lib/tracking";
import { currentLanguage } from "../../i18n";
import styles from "./PublicTermsPage.module.css";

const DIVISION_CODE = "ACCOUNT_DELETION";
/**
 * 삭제 요청을 받을 주소 — 본문이 어드민(약관 관리)에 등록되기 전까지만 쓰이는 fallback.
 *
 * ⚠ design 2026-08-20: 템플릿 자리표시자(help@template.kr)였다. 있지도 않은 도메인이
 *   **스토어 심사용 공개 페이지**에 그대로 나가고 있었다. 앱 푸터가 이미 쓰고 있는
 *   고객센터 주소(Footer.tsx FALLBACK_COMPANY_INFO)로 맞춘다 — 앱 안과 밖이 다른
 *   주소를 말하면 안 된다.
 * ⚠ 이 주소가 실제로 삭제 요청을 받는 창구가 맞는지는 **운영에서 확인이 필요하다.**
 */
const CS_EMAIL = "support@duckflex.kr";

const FALLBACK_BODY = `Peeka(kr.duckflex.peeka.app)의 계정과 개인정보 삭제를 요청하는 방법을 안내합니다.

■ 앱에서 직접 삭제 (권장)
1. 앱을 실행하고 로그인합니다.
2. 내 정보 화면 아래쪽의 회원 탈퇴로 이동합니다.
3. 안내에 따라 탈퇴 사유를 선택하고 탈퇴를 완료합니다.
탈퇴 즉시 계정이 비활성화되고 모든 로그인 세션이 종료됩니다.

■ 앱을 이용할 수 없는 경우
가입에 사용한 소셜 계정(Google 또는 Apple)과 그 계정의 이메일 주소를 적어
아래로 삭제를 요청해 주세요.
- 고객센터 이메일: ${CS_EMAIL}

■ 삭제되는 데이터
회원 탈퇴 시 이메일 주소, 프로필 이름, 소셜 계정 식별자 등 개인정보와
컬렉션·관심 카드·스캔 기록은 탈퇴 후 7일 이내에 파기되거나 개인을 식별할 수 없도록
익명화 처리됩니다.

■ 관계 법령에 따라 일정 기간 보관되는 데이터
결제·거래 기록이 있는 경우, 「전자상거래 등에서의 소비자보호에 관한 법률」 등
관계 법령에 따라 5년간 보관된 후 파기됩니다.`;

export default function AccountDeletionPage() {
  // 번역본이 없으면 BE 가 정본(한국어)을 내려준다.
  const { t, i18n } = useTranslation();
  const language = useMemo(() => currentLanguage(), [i18n.language]);
  const { data: latest, isLoading } = useLatestTermsQuery(DIVISION_CODE, language);

  useEffect(() => {
    track("terms_view", { type: "account_deletion" });
  }, []);

  const bodyHtml = latest?.bodyContent;
  const showLoading = isLoading && !bodyHtml;

  return (
    <main className={styles.root}>
      <article className={styles.doc}>
        <h1 className={styles.title}>{t("legal.accountDeletionTitle")}</h1>
        {showLoading ? (
          <p className={styles.muted}>{t("state.loading")}</p>
        ) : bodyHtml ? (
          <div
            className={styles.body}
            // BE sanitize + FE DOMPurify 이중 방어.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }}
          />
        ) : (
          <div className={styles.fallbackBody}>{FALLBACK_BODY}</div>
        )}
        <footer className={styles.footer}>Peeka · kr.duckflex.peeka.app</footer>
      </article>
    </main>
  );
}
